export interface TelegramThemeParams {
  bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
  secondary_bg_color?: string;
  header_bg_color?: string;
  accent_text_color?: string;
  section_bg_color?: string;
  section_header_text_color?: string;
  subtitle_text_color?: string;
  destructive_text_color?: string;
}

export interface TelegramWebApp {
  initData: string;
  initDataUnsafe: Record<string, unknown>;
  colorScheme: 'light' | 'dark';
  themeParams: TelegramThemeParams;
  isExpanded: boolean;
  viewportHeight: number;
  viewportStableHeight: number;
  platform: string;
  version: string;
  ready(): void;
  expand(): void;
  close(): void;
  openLink(url: string, options?: { try_instant_view?: boolean }): void;
  openTelegramLink(url: string): void;
  showAlert(message: string, callback?: () => void): void;
  showConfirm(message: string, callback?: (ok: boolean) => void): void;
  showPopup(
    params: { title?: string; message: string; buttons?: Array<{ id?: string; type?: string; text?: string }> },
    callback?: (buttonId: string) => void,
  ): void;
  HapticFeedback?: {
    impactOccurred(style: string): void;
    notificationOccurred(type: 'error' | 'success' | 'warning'): void;
    selectionChanged(): void;
  };
  BackButton: { show(): void; hide(): void; onClick(cb: () => void): void; offClick(cb: () => void): void };
  MainButton: {
    text: string;
    show(): void;
    hide(): void;
    enable(): void;
    disable(): void;
    showProgress(leaveActive?: boolean): void;
    hideProgress(): void;
    setText(text: string): void;
    onClick(cb: () => void): void;
    offClick(cb: () => void): void;
  };
  setHeaderColor(color: string): void;
  setBackgroundColor(color: string): void;
  onEvent(event: string, cb: () => void): void;
  offEvent(event: string, cb: () => void): void;
}

declare global {
  interface Window {
    Telegram?: { WebApp: TelegramWebApp };
  }
}

export function tg(): TelegramWebApp | null {
  return window.Telegram?.WebApp ?? null;
}

/**
 * `initDataUnsafe` is never used for identity. The raw `initData` string is sent
 * to the backend, which verifies its HMAC before issuing a session.
 */
export function rawInitData(): string {
  const app = tg();
  if (app?.initData) return app.initData;
  // Development fallback: a value supplied by the dev harness, still verified
  // server-side exactly like a real one.
  return new URLSearchParams(window.location.search).get('initData') ?? '';
}

export function applyTheme(): void {
  const app = tg();
  if (!app) return;
  const root = document.documentElement;
  const params = app.themeParams;

  const set = (name: string, value: string | undefined) => {
    if (value) root.style.setProperty(name, value);
  };
  set('--tg-bg', params.bg_color);
  set('--tg-text', params.text_color);
  set('--tg-hint', params.hint_color);
  set('--tg-link', params.link_color);
  set('--tg-button', params.button_color);
  set('--tg-button-text', params.button_text_color);
  set('--tg-secondary-bg', params.secondary_bg_color);
  set('--tg-section-bg', params.section_bg_color);
  set('--tg-subtitle', params.subtitle_text_color);
  set('--tg-destructive', params.destructive_text_color);
  root.dataset.theme = app.colorScheme;
}

export function haptic(type: 'success' | 'error' | 'warning' | 'select' = 'select'): void {
  const feedback = tg()?.HapticFeedback;
  if (!feedback) return;
  if (type === 'select') feedback.selectionChanged();
  else feedback.notificationOccurred(type);
}

export function confirmAction(message: string): Promise<boolean> {
  const app = tg();
  if (!app?.showConfirm) return Promise.resolve(window.confirm(message));
  return new Promise((resolve) => app.showConfirm(message, resolve));
}

export function alertMessage(message: string): void {
  const app = tg();
  if (app?.showAlert) app.showAlert(message);
  else window.alert(message);
}
